import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  InboxIcon,
  PlusIcon,
  SendIcon,
  Trash2Icon,
  WebhookIcon,
} from "lucide-react";

import { ProjectTabs } from "../components/ProjectTabs";
import { Topbar } from "../components/Topbar";
import { WebhookDialog } from "../components/WebhookDialog";
import { ProjectNotFound } from "../components/ErrorState";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../components/ui/empty";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Page, PageBody, PageHeader, PageTitle, PageDescription } from "../components/PageLayout";
import {
  useCreateWebhook,
  useDeleteWebhook,
  useProject,
  useTestWebhook,
  useUpdateWebhook,
  useWebhooks,
} from "../app/queries";
import { useConfirmAction } from "../hooks/useConfirmAction";
import { copyToClipboard } from "../lib/clipboard";
import { relativeTime } from "../lib/format";
import { cn } from "../lib/utils";
import type { WebhookSummary } from "../lib/api";

export function ProjectWebhooksPage() {
  const { id = "" } = useParams();
  const projects = useProject(id);
  const project = projects.project;
  const webhooks = useWebhooks(id);

  const [editing, setEditing] = useState<WebhookSummary | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  // Captured pre-mutation so the delete toast can name the webhook even
  // after the cache invalidates and the row leaves `items`.
  const deletingNameRef = useRef<string | null>(null);

  const create = useCreateWebhook(id, {
    success: () => `Created webhook`,
    error: (err) =>
      err instanceof Error ? err.message : "Failed to create webhook",
    onSuccess: () => {
      setDialogOpen(false);
    },
  });
  const update = useUpdateWebhook(id, {
    success: (hook) => `Saved ${hook.name}`,
    error: (err) =>
      err instanceof Error ? err.message : "Failed to update webhook",
  });
  const remove = useDeleteWebhook(id, {
    success: () =>
      `Deleted webhook ${deletingNameRef.current ?? ""}`.trimEnd() || "Deleted webhook",
    error: (err) =>
      err instanceof Error ? err.message : "Failed to delete webhook",
  });
  const sendTest = useTestWebhook(id, {
    success: (res) =>
      res.error
        ? `Receiver responded ${res.status}: ${res.error}`
        : `Test event delivered (status ${res.status})`,
    error: (err) =>
      err instanceof Error ? err.message : "Failed to send test event",
  });

  // Wrap the destructive actions with a native confirm prompt.
  // useConfirmAction handles the prompt, busy state, and error
  // swallow so the row can stay focused on its own UI.
  const deleteAction = useConfirmAction(
    (webhookId: string) => {
      deletingNameRef.current =
        items.find((h) => h.id === webhookId)?.name ?? null;
      return remove.mutateAsync(webhookId);
    },
    (webhookId: string) => {
      const target = items.find((h) => h.id === webhookId);
      return `Delete the webhook "${target?.name ?? webhookId}"? Discord will stop receiving notifications.`;
    },
  );
  const testAction = useConfirmAction(
    (webhookId: string) => sendTest.mutateAsync(webhookId),
    (webhookId: string) => {
      const target = items.find((h) => h.id === webhookId);
      return `Send a test event to "${target?.name ?? webhookId}"?`;
    },
  );

  const items = webhooks.data?.webhooks ?? [];
  const hasItems = items.length > 0;

  if (projects.isLoading) {
    return (
      <Page>
        <Topbar title="…" />
        <ProjectTabs />
        <PageBody width="wide">
          <Skeleton className="h-32 w-full" />
        </PageBody>
      </Page>
    );
  }

  if (!project) {
    return (
      <ProjectNotFound
        breadcrumb={[
          { label: "Projects", to: "/" },
          { label: "Not found" },
        ]}
      />
    );
  }

  return (
    <Page>
      <Topbar
        breadcrumb={[
          { label: "Projects", to: "/" },
          { label: project.name, to: `/projects/${id}` },
          { label: "Webhooks" },
        ]}
      />
      <ProjectTabs />
      <PageHeader>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <PageTitle>Webhooks</PageTitle>
            <PageDescription>
              Send new bug reports to Discord (and any other HTTP service we
              add later) the moment they arrive.
            </PageDescription>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setDialogError(null);
              setDialogOpen(true);
            }}
            data-testid="open-create-webhook"
          >
            <PlusIcon /> New webhook
          </Button>
        </div>
      </PageHeader>

      <PageBody width="wide">
        {webhooks.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : webhooks.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not load webhooks</AlertTitle>
            <AlertDescription>{webhooks.error.message}</AlertDescription>
          </Alert>
        ) : !hasItems ? (
          <Empty className="rounded-xl border bg-card">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <WebhookIcon />
              </EmptyMedia>
              <EmptyTitle>No webhooks yet</EmptyTitle>
              <EmptyDescription>
                Connect a Discord channel to receive a formatted embed for every
                new report. Mushi signs every request with a per-webhook
                secret so the receiver can verify its origin.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                onClick={() => {
                  setEditing(null);
                  setDialogError(null);
                  setDialogOpen(true);
                }}
                data-testid="open-create-webhook-empty"
              >
                <PlusIcon /> Add Discord webhook
              </Button>
              <Button
                variant="link"
                size="sm"
                onClick={() =>
                  window.open(
                    "https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks",
                    "_blank",
                    "noreferrer,noopener",
                  )
                }
              >
                Where do I find this URL?
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <div className="flex flex-col gap-4">
            {items.map((hook) => (
              <WebhookRow
                key={hook.id}
                hook={hook}
                outcome={formatOutcome(hook)}
                onEdit={() => {
                  setEditing(hook);
                  setDialogError(null);
                  setDialogOpen(true);
                }}
                onDelete={() => deleteAction.confirm(hook.id)}
                onSendTest={() => testAction.confirm(hook.id)}
                busy={
                  update.isPending ||
                  deleteAction.busy ||
                  testAction.busy
                }
              />
            ))}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>How payloads are signed</CardTitle>
            <CardDescription>
              Mushi adds three headers to every request. The signature is an
              HMAC-SHA256 of the body using the secret you provided.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
              <li>
                <code className="font-mono text-foreground">X-Mushi-Event</code>{" "}
                — the event name, currently always{" "}
                <code className="font-mono">report.created</code>.
              </li>
              <li>
                <code className="font-mono text-foreground">X-Mushi-Signature</code>{" "}
                —{" "}
                <code className="font-mono">
                  hex(hmac_sha256(secret, body))
                </code>
                .
              </li>
              <li>
                <code className="font-mono text-foreground">X-Mushi-Webhook-Id</code>{" "}
                — the webhook's id, useful for log correlation.
              </li>
            </ul>
            <Button
              variant="link"
              size="sm"
              className="mt-3 h-auto px-0"
              onClick={async () => {
                const sample = `{
  "username": "Mushi",
  "embeds": [{
    "title": "New report: Crash on save",
    "description": "**HIGH** · DEMO-1",
    "color": 14719934,
    "fields": [
      {"name": "Project", "value": "Demo (\`demo\`)", "inline": true},
      {"name": "Platform", "value": "windows", "inline": true},
      {"name": "Version", "value": "1.4.2", "inline": true}
    ],
    "url": "https://mushi.example.com/#/projects/.../reports/..."
  }],
  "event": "report.created"
}`;
                const result = await copyToClipboard(sample);
                if (result.ok) {
                  toast.success("Sample payload copied");
                }
              }}
            >
              Copy a sample Discord payload
            </Button>
          </CardContent>
        </Card>
      </PageBody>

      <WebhookDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setDialogError(null);
            setEditing(null);
          }
        }}
        webhook={editing}
        busy={create.isPending || update.isPending}
        error={dialogError}
        onSubmit={async (input) => {
          try {
            if (editing) {
              const patch: {
                name?: string;
                url?: string;
                secret?: string;
                enabled?: boolean;
              } = { name: input.name, url: input.url, enabled: input.enabled };
              if (input.secret) {
                patch.secret = input.secret;
              }
              await update.mutateAsync({ webhookId: editing.id, patch });
              setDialogOpen(false);
            } else {
              await create.mutateAsync({
                name: input.name,
                type: "discord",
                url: input.url,
                secret: input.secret,
              });
              // The dialog stays open so the user can copy the secret;
              // the success callback above also closes it after copying
              // the secret into the banner.
            }
          } catch (err) {
            setDialogError(
              err instanceof Error ? err.message : "Failed to save webhook",
            );
          }
        }}
      />
    </Page>
  );
}

interface WebhookRowProps {
  hook: WebhookSummary;
  outcome: { tone: "ok" | "warn" | "error" | "idle"; label: string };
  onEdit: () => void;
  onDelete: () => void;
  onSendTest: () => void;
  busy: boolean;
}

function WebhookRow({
  hook,
  outcome,
  onEdit,
  onDelete,
  onSendTest,
  busy,
}: WebhookRowProps) {
  return (
    <Card data-testid={`webhook-row-${hook.id}`}>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm font-semibold text-foreground">
                {hook.name}
              </span>
              <Badge
                variant={hook.enabled ? "secondary" : "outline"}
                className={cn(
                  "text-xs",
                  hook.enabled
                    ? "border-success/30 bg-success/10 text-success"
                    : "text-muted-foreground",
                )}
              >
                {hook.enabled ? "enabled" : "disabled"}
              </Badge>
              <Badge variant="outline" className="text-xs uppercase tracking-wide text-muted-foreground">
                {hook.type}
              </Badge>
            </div>
            <code
              className="truncate font-mono text-xs text-muted-foreground"
              title={hook.url}
            >
              {hook.url}
            </code>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>
                Events:{" "}
                <span className="text-foreground/80">
                  {hook.events.join(", ")}
                </span>
              </span>
              <span>
                Created:{" "}
                <span className="text-foreground/80">
                  {relativeTime(hook.createdAt)}
                </span>
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onSendTest}
              disabled={busy}
              data-testid={`webhook-test-${hook.id}`}
            >
              <SendIcon /> Send test
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onEdit}
              disabled={busy}
              data-testid={`webhook-edit-${hook.id}`}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={onDelete}
              disabled={busy}
              data-testid={`webhook-delete-${hook.id}`}
            >
              <Trash2Icon /> Delete
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span
            className={cn(
              "inline-block size-2 rounded-full",
              outcome.tone === "ok" && "bg-success",
              outcome.tone === "warn" && "bg-severity-medium",
              outcome.tone === "error" && "bg-destructive",
              outcome.tone === "idle" && "bg-muted-foreground/50",
            )}
            aria-hidden="true"
          />
          <span className="text-muted-foreground">{outcome.label}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function formatOutcome(hook: WebhookSummary): {
  tone: "ok" | "warn" | "error" | "idle";
  label: string;
} {
  if (!hook.lastTriggeredAt) {
    return { tone: "idle", label: "No events dispatched yet." };
  }
  if (hook.lastStatus == null) {
    return {
      tone: "warn",
      label: `Last attempt: ${relativeTime(hook.lastTriggeredAt)} · unknown status`,
    };
  }
  if (hook.lastStatus >= 200 && hook.lastStatus < 300) {
    return {
      tone: "ok",
      label: `Last delivery: ${relativeTime(hook.lastTriggeredAt)} · status ${hook.lastStatus}`,
    };
  }
  return {
    tone: "error",
    label: `Last delivery: ${relativeTime(hook.lastTriggeredAt)} · status ${hook.lastStatus}${
      hook.lastError ? ` (${hook.lastError})` : ""
    }`,
  };
}

// Re-export an icon the dialog might want to drop into breadcrumbs.
export { InboxIcon };
